import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ComponentProps, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Input, Spin, Typography } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { searchOrganizations } from '../features/organizations/api';
import { useTaskUsers, UserAvatar } from '../pages/tasks/TaskBits';

export interface MentionOption {
  kind: 'user' | 'organization';
  id: string;
  name: string;
  /** link the mention points at in rich text */
  href: string;
  sub?: string;
}

/** The `@query` right before the caret, or null. A query may hold one space so "@Jane Do" still matches. */
export function mentionQuery(textBeforeCaret: string): string | null {
  const m = /(?:^|[\s(])@([^\s@][^@\n]{0,40}|)$/u.exec(textBeforeCaret);
  if (!m) return null;
  const q = m[1];
  return (q.match(/ /g)?.length ?? 0) > 1 || q.endsWith('  ') ? null : q;
}

/** Members (from the cached user list) and organizations (searched) matching `query`; `null` keeps it idle. */
export function useMentionOptions(query: string | null) {
  const { list } = useTaskUsers();
  const [orgs, setOrgs] = useState<MentionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const active = query !== null;
  const q = (query ?? '').trim().toLowerCase();

  useEffect(() => {
    if (!active) return;
    let stale = false;
    setLoading(true);
    const t = window.setTimeout(() => {
      searchOrganizations(q)
        .then((items) => !stale && setOrgs(items.slice(0, 6).map((o) => ({ kind: 'organization', id: o.id, name: o.name, href: `/organizations/${o.id}` }))))
        .catch(() => !stale && setOrgs([]))
        .finally(() => !stale && setLoading(false));
    }, 200);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [q, active]);

  const users = useMemo<MentionOption[]>(
    () =>
      list
        .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
        .slice(0, 6)
        .map((u) => ({ kind: 'user', id: u.id, name: u.name, href: `mailto:${u.email}`, sub: u.email })),
    [list, q],
  );
  return { options: active ? [...users, ...orgs] : [], loading: active && loading };
}

/** Keyboard + selection state shared by the rich-text and plain-text hosts. */
export function useMentionMenu(query: string | null, onPick: (o: MentionOption) => void, onClose: () => void) {
  const { options, loading } = useMentionOptions(query);
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [query]);
  const open = query !== null && (options.length > 0 || loading || !query.includes(' '));
  const state = useRef({ open, options, index, onPick, onClose });
  state.current = { open, options, index, onPick, onClose };
  /** Handles a key while the menu is open; true when it was consumed. */
  const handleKey = (key: string) => {
    const s = state.current;
    if (!s.open) return false;
    if (key === 'Escape') return s.onClose(), true;
    if (!s.options.length) return false;
    if (key === 'ArrowDown') return setIndex((s.index + 1) % s.options.length), true;
    if (key === 'ArrowUp') return setIndex((s.index - 1 + s.options.length) % s.options.length), true;
    if (key === 'Enter' || key === 'Tab') return s.onPick(s.options[s.index]), true;
    return false;
  };
  return { open, options, loading, index, setIndex, handleKey };
}

/** The floating list of matches, placed under the caret (viewport coordinates). */
export function MentionMenu({ at, options, loading, index, onHover, onPick }: { at: { left: number; top: number }; options: MentionOption[]; loading: boolean; index: number; onHover: (i: number) => void; onPick: (o: MentionOption) => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(at);
  // keep it on screen: flip above the caret near the bottom, shift left near the right edge
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return setPos(at);
    const { width, height } = el.getBoundingClientRect();
    const left = Math.max(8, Math.min(at.left, window.innerWidth - width - 8));
    const top = at.top + height + 8 > window.innerHeight ? Math.max(8, at.top - height - 28) : at.top;
    setPos({ left, top });
  }, [at.left, at.top, options.length, loading]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  let lastKind: MentionOption['kind'] | null = null;
  return createPortal(
    <div ref={listRef} className="mention-menu" role="listbox" aria-label="Mention someone" style={{ left: pos.left, top: pos.top }} onMouseDown={(e) => e.preventDefault()}>
      {options.map((o, i) => {
        const header = o.kind !== lastKind ? <div className="mention-menu-group">{o.kind === 'user' ? 'Members' : 'Organizations'}</div> : null;
        lastKind = o.kind;
        return (
          <div key={`${o.kind}-${o.id}`}>
            {header}
            <div role="option" aria-selected={i === index} data-index={i} className={`mention-menu-item${i === index ? ' mention-menu-item-active' : ''}`} onMouseEnter={() => onHover(i)} onClick={() => onPick(o)}>
              {o.kind === 'user' ? (
                <UserAvatar user={{ id: o.id, name: o.name }} size={22} />
              ) : (
                <span className="mention-menu-org">
                  <BankOutlined />
                </span>
              )}
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.name}
                {o.sub && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                    {o.sub}
                  </Typography.Text>
                )}
              </span>
            </div>
          </div>
        );
      })}
      {loading && (
        <div className="mention-menu-empty">
          <Spin size="small" />
        </div>
      )}
      {!loading && options.length === 0 && <div className="mention-menu-empty">No matching members or organizations</div>}
    </div>,
    document.body,
  );
}

/** Viewport position just below the caret of a textarea, measured with an offscreen mirror. */
function caretPosition(el: HTMLTextAreaElement, pos: number) {
  const style = window.getComputedStyle(el);
  const mirror = document.createElement('div');
  for (const p of ['boxSizing', 'width', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing', 'textIndent', 'tabSize'] as const) {
    mirror.style[p] = style[p];
  }
  Object.assign(mirror.style, { position: 'absolute', visibility: 'hidden', top: '0', left: '-9999px', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' });
  mirror.textContent = el.value.slice(0, pos);
  const marker = document.createElement('span');
  marker.textContent = '​';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const rect = el.getBoundingClientRect();
  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;
  const left = rect.left + marker.offsetLeft - el.scrollLeft;
  const top = rect.top + marker.offsetTop - el.scrollTop + lineHeight + 2;
  document.body.removeChild(mirror);
  return { left, top };
}

type TextAreaProps = Omit<ComponentProps<typeof Input.TextArea>, 'value' | 'onChange'> & {
  value?: string | null;
  onChange?: (e: { target: { value: string } }) => void;
};

/**
 * antd TextArea where typing `@` opens a menu of members and organizations; picking one inserts `@Name`.
 * A drop-in replacement: works with Form.Item's value/onChange.
 */
export const MentionTextArea = forwardRef<TextAreaRef, TextAreaProps>(function MentionTextArea({ value, onChange, onKeyDown, onBlur, ...rest }, ref) {
  const inner = useRef<TextAreaRef>(null);
  useImperativeHandle(ref, () => inner.current as TextAreaRef);
  const [query, setQuery] = useState<string | null>(null);
  const [at, setAt] = useState({ left: 0, top: 0 });
  const text = value ?? '';
  const pendingCaret = useRef<number | null>(null);

  const el = () => inner.current?.resizableTextArea?.textArea ?? null;
  const detect = () => {
    const ta = el();
    if (!ta || ta.selectionStart !== ta.selectionEnd) return setQuery(null);
    const q = mentionQuery(ta.value.slice(0, ta.selectionStart));
    setQuery(q);
    if (q !== null) setAt(caretPosition(ta, ta.selectionStart - q.length - 1));
  };
  const pick = (o: MentionOption) => {
    const ta = el();
    if (!ta || query === null) return;
    const end = ta.selectionStart;
    const start = end - query.length - 1;
    const insert = `@${o.name} `;
    pendingCaret.current = start + insert.length;
    onChange?.({ target: { value: ta.value.slice(0, start) + insert + ta.value.slice(end) } });
    setQuery(null);
  };
  const menu = useMentionMenu(query, pick, () => setQuery(null));

  // put the caret after an inserted mention once the new value has rendered
  useLayoutEffect(() => {
    const ta = el();
    if (ta && pendingCaret.current !== null) {
      ta.focus();
      ta.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [text]);

  return (
    <>
      <Input.TextArea
        ref={inner}
        value={text}
        onChange={(e) => {
          onChange?.(e);
          requestAnimationFrame(detect);
        }}
        onKeyDown={(e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
          if (menu.handleKey(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onKeyDown?.(e);
        }}
        onKeyUp={(e) => ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) && detect()}
        onClick={detect}
        onBlur={(e) => {
          setQuery(null);
          onBlur?.(e);
        }}
        {...rest}
      />
      {menu.open && <MentionMenu at={at} options={menu.options} loading={menu.loading} index={menu.index} onHover={menu.setIndex} onPick={pick} />}
    </>
  );
});
